import querystring from 'query-string'

/**
 * 非同期でスリープする
 * @param milliseconds スリープ時間(ミリ秒)
 */
export const sleep = (milliSeconds: number) =>
  new Promise((resolve) => setTimeout(resolve, milliSeconds))

/** 共通レスポンス */
export interface CommonResponse<T = null> {
  /** HTTPステータスコード */
  status: number
  /** エラーオブジェクト */
  error?: { message: string }
  /** データ格納部 */
  payload: T
}

/**
 * リトライ機能付き fetch
 * @param input RequestInfo
 * @param init RequestInit
 * @param retryCount リトライ回数 (default:3)
 * @param delay リトライ間隔（ミリ秒） (default:2000ms)
 */
const fetchRetry = async (
  input: RequestInfo,
  init?: RequestInit,
  retryCount: number = 3,
  delay: number = 2000
): Promise<Response> => {
  if (retryCount < 1) throw Error('invalid retry count')
  try {
    return await fetch(input, init)
  } catch (err) {
    if (retryCount <= 1) throw err
    await sleep(delay)
    return await fetchRetry(input, init, retryCount - 1, delay)
  }
}

/** APIリクエスト全体で使う設定 */
const baseOptions: RequestInit = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  credentials: 'same-origin',
  // iOS でcookie がうまく使用されないバグ修正に必要
  // refs https://github.com/github/fetch/issues/349 https://forums.developer.apple.com/thread/89050
  cache: 'no-store',
}

async function handleResponse<T = null>(res: Response): Promise<CommonResponse<T>> {
  const body = await res.json()
  return { status: res.status, ...body }
}

export function fetchGet<T>(url: string, params?: { [key: string]: any }, options?: RequestInit) {
  // IE がキャッシュしちゃうのでタイムスタンプを付与
  // refs https://jira.kabumap.tokyo/jira/browse/P1900501-111
  const qst = querystring.stringify(
    { _d: new Date().getTime(), ...params },
    { arrayFormat: 'comma' }
  )
  const mergedOptions = {
    ...baseOptions,
    method: 'GET',
    ...options,
  }
  return fetchRetry(`${url}${url.endsWith('?') ? '' : '?'}${qst}`, mergedOptions).then((res) =>
    handleResponse<T>(res)
  )
}

export function fetchPost<T>(url: string, params?: Record<string, any>, options?: RequestInit) {
  const mergedOptions = {
    ...baseOptions,
    method: 'POST',
    body: JSON.stringify(params),
    ...options,
  }
  return fetchRetry(url, mergedOptions).then((res) => handleResponse<T>(res))
}

export function fetchPut<T>(url: string, params?: Record<string, any>, options?: RequestInit) {
  const mergedOptions = {
    ...baseOptions,
    method: 'PUT',
    body: JSON.stringify(params),
    ...options,
  }
  return fetchRetry(url, mergedOptions).then((res) => handleResponse<T>(res))
}

export function fetchDelete<T>(
  url: string,
  params?: { [key: string]: any },
  options?: RequestInit
) {
  // IE がキャッシュしちゃうのでタイムスタンプを付与
  // refs https://jira.kabumap.tokyo/jira/browse/P1900501-111
  const qst = querystring.stringify(
    { _d: new Date().getTime(), ...params },
    { arrayFormat: 'comma' }
  )
  const mergedOptions = {
    ...baseOptions,
    method: 'DELETE',
    ...options,
  }
  return fetchRetry(`${url}${url.endsWith('?') ? '' : '?'}${qst}`, mergedOptions).then((res) =>
    handleResponse<T>(res)
  )
}
